/**
 * DatePicker — Custom date picker styled to match the app's dark theme.
 *
 * Supports month/year dropdowns for fast navigation and typed input
 * (YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, "14 June 2002").
 * Calendar dropdown renders via portal above modals / overflow containers.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import X from 'lucide-react/dist/esm/icons/x';

const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const DEFAULT_MIN_YEAR = 1900;
const DEFAULT_MAX_YEAR_OFFSET = 10;

interface DatePickerProps {
  value: string; // "YYYY-MM-DD" or ""
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  minDate?: string; // "YYYY-MM-DD"
  maxDate?: string; // "YYYY-MM-DD"
  className?: string;
}

function parseDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplay(str: string): string {
  const d = parseDate(str);
  if (!d) return '';
  return `${d.getDate()} ${MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`;
}

function isValidYmd(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/** Parse typed input into YYYY-MM-DD, or null if unparseable. */
function parseTypedDate(raw: string): string | null {
  const text = raw.trim();
  if (!text) return '';

  // YYYY-MM-DD
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    return isValidYmd(y, m, d) ? formatDate(new Date(y, m - 1, d)) : null;
  }

  // MM/DD/YYYY or DD/MM/YYYY (and - or . separators)
  const slash = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (slash) {
    const a = Number(slash[1]);
    const b = Number(slash[2]);
    const y = Number(slash[3]);
    // Prefer US MM/DD when ambiguous; if first part > 12 treat as DD/MM
    if (a > 12 && b <= 12 && isValidYmd(y, b, a)) {
      return formatDate(new Date(y, b - 1, a));
    }
    if (isValidYmd(y, a, b)) {
      return formatDate(new Date(y, a - 1, b));
    }
    if (b > 12 && a <= 12 && isValidYmd(y, a, b) === false && isValidYmd(y, b, a)) {
      return formatDate(new Date(y, b - 1, a));
    }
    return null;
  }

  // "14 June 2002" / "June 14, 2002" / "14 Jun 2002"
  const named = text.match(
    /^(?:(\d{1,2})\s+([A-Za-z]+)(?:,?\s*)(\d{4})|([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4}))$/
  );
  if (named) {
    const day = Number(named[1] || named[5]);
    const monthToken = (named[2] || named[4]).toLowerCase();
    const y = Number(named[3] || named[6]);
    const miFull = MONTH_FULL.findIndex(m => m.toLowerCase() === monthToken);
    const miShort = MONTH_SHORT.findIndex(m => m.toLowerCase() === monthToken);
    const m = miFull >= 0 ? miFull : miShort;
    if (m >= 0 && isValidYmd(y, m + 1, day)) {
      return formatDate(new Date(y, m, day));
    }
  }

  return null;
}

function getCalendarDays(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

function yearFromIso(iso?: string, fallback?: number): number | undefined {
  if (!iso) return fallback;
  const y = Number(iso.slice(0, 4));
  return Number.isFinite(y) ? y : fallback;
}

const selectClassName = `
  appearance-none bg-surface-secondary border border-subtle rounded-lg
  text-xs font-semibold text-content px-2 py-1.5 pr-6
  outline-none cursor-pointer
  hover:border-default focus:border-brand/40
  bg-[length:12px] bg-[right_6px_center] bg-no-repeat
`.replace(/\s+/g, ' ').trim();

const SELECT_CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`;

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, placeholder, size = 'md', minDate, maxDate, className }) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, openAbove: false });
  const [inputText, setInputText] = useState(() => formatDisplay(value));
  const [inputFocused, setInputFocused] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => formatDate(today), [today]);

  const yearOptions = useMemo(() => {
    const minY = yearFromIso(minDate, DEFAULT_MIN_YEAR)!;
    const maxY = yearFromIso(maxDate, today.getFullYear() + DEFAULT_MAX_YEAR_OFFSET)!;
    const start = Math.min(minY, maxY);
    const end = Math.max(minY, maxY);
    const years: number[] = [];
    for (let y = end; y >= start; y--) years.push(y);
    // Ensure current view year is always in the list (e.g. value outside range while editing)
    if (!years.includes(viewYear)) {
      years.push(viewYear);
      years.sort((a, b) => b - a);
    }
    return years;
  }, [minDate, maxDate, today, viewYear]);

  // Keep input text in sync when value changes externally (and input isn't being edited)
  useEffect(() => {
    if (!inputFocused) {
      setInputText(formatDisplay(value));
    }
  }, [value, inputFocused]);

  // Sync view to value when opening + position dropdown
  useEffect(() => {
    if (!open) return;
    const d = parseDate(value);
    if (d) {
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const calendarWidth = 300;
      const calendarHeight = 360;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < calendarHeight && rect.top > calendarHeight;
      const leftAligned = rect.left;
      const rightAligned = rect.right - calendarWidth;
      const wouldOverflowRight = leftAligned + calendarWidth + 8 > window.innerWidth;
      const left = Math.max(8, wouldOverflowRight ? rightAligned : leftAligned);
      setDropdownPos({
        top: openAbove ? rect.top - calendarHeight - 8 : rect.bottom + 8,
        left,
        openAbove,
      });
    }
  }, [open, value]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  const isOutOfRange = useCallback((dateStr: string): boolean => {
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  }, [minDate, maxDate]);

  const isDisabled = useCallback((day: number): boolean => {
    return isOutOfRange(formatDate(new Date(viewYear, viewMonth, day)));
  }, [viewYear, viewMonth, isOutOfRange]);

  const commitValue = useCallback((next: string) => {
    if (next && isOutOfRange(next)) return false;
    onChange(next);
    setInputText(formatDisplay(next));
    return true;
  }, [onChange, isOutOfRange]);

  const handleSelect = useCallback((day: number) => {
    if (isDisabled(day)) return;
    const d = new Date(viewYear, viewMonth, day);
    commitValue(formatDate(d));
    setOpen(false);
  }, [viewYear, viewMonth, isDisabled, commitValue]);

  const commitTypedInput = useCallback(() => {
    const parsed = parseTypedDate(inputText);
    if (parsed === null) {
      // Invalid — revert to last committed value
      setInputText(formatDisplay(value));
      return;
    }
    if (parsed === '') {
      onChange('');
      setInputText('');
      return;
    }
    if (!commitValue(parsed)) {
      setInputText(formatDisplay(value));
      return;
    }
    const d = parseDate(parsed);
    if (d) {
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [inputText, value, onChange, commitValue]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const openCalendar = () => setOpen(true);

  const calendarDropdown = open ? createPortal(
    <div
      ref={dropdownRef}
      data-datepicker-portal
      onMouseDown={(e) => {
        // Keep input focus when clicking days/nav; allow native <select> to open
        const tag = (e.target as HTMLElement).tagName;
        if (tag !== 'SELECT' && tag !== 'OPTION') {
          e.preventDefault();
        }
      }}
      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}
      className="w-[300px] p-3 rounded-xl bg-surface-page border border-default shadow-[0_20px_50px_rgba(0,0,0,0.7)] animate-scale-in"
    >
      {/* Month / Year nav */}
      <div className="flex items-center gap-1 mb-3">
        <button
          type="button"
          onClick={prevMonth}
          aria-label="Previous month"
          className="p-1.5 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-secondary transition-colors cursor-pointer border-none outline-none shrink-0"
        >
          <ChevronLeft size={14} />
        </button>

        <div className="flex-1 flex items-center justify-center gap-1.5 min-w-0">
          <select
            value={viewMonth}
            onChange={(e) => setViewMonth(Number(e.target.value))}
            aria-label="Month"
            className={selectClassName}
            style={{ backgroundImage: SELECT_CHEVRON, maxWidth: '58%' }}
          >
            {MONTH_FULL.map((name, i) => (
              <option key={name} value={i}>{name}</option>
            ))}
          </select>
          <select
            value={viewYear}
            onChange={(e) => setViewYear(Number(e.target.value))}
            aria-label="Year"
            className={selectClassName}
            style={{ backgroundImage: SELECT_CHEVRON, maxWidth: '42%' }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={nextMonth}
          aria-label="Next month"
          className="p-1.5 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-secondary transition-colors cursor-pointer border-none outline-none shrink-0"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_HEADERS.map(d => (
          <div key={d} className="text-center text-[10px] text-content-faint font-medium py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-0">
        {days.map((day, i) => {
          if (day === null) {
            return <div key={`empty-${i}`} className="h-8" />;
          }

          const dateStr = formatDate(new Date(viewYear, viewMonth, day));
          const isSelected = value === dateStr;
          const isToday = dateStr === todayStr;
          const disabled = isDisabled(day);

          return (
            <button
              key={`day-${day}`}
              type="button"
              onClick={() => handleSelect(day)}
              disabled={disabled}
              className={`
                h-8 w-full flex items-center justify-center rounded-lg text-xs font-medium
                transition-all border-none outline-none
                ${disabled
                  ? 'text-content-faint cursor-not-allowed opacity-40'
                  : isSelected
                    ? 'bg-brand text-content cursor-pointer'
                    : isToday
                      ? 'bg-brand/10 text-primary hover:bg-brand/20 cursor-pointer'
                      : 'text-content-secondary hover:bg-surface-secondary hover:text-content cursor-pointer'
                }
              `}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-2 pt-2 border-t border-subtle flex items-center justify-between gap-2">
        <span className="text-[10px] text-content-faint px-1">Type or pick a date</span>
        <button
          type="button"
          onClick={() => { commitValue(todayStr); setOpen(false); }}
          disabled={isOutOfRange(todayStr)}
          className="text-[10px] text-brand/60 hover:text-primary font-medium py-1 px-2 rounded-lg hover:bg-brand/5 transition-colors cursor-pointer border-none outline-none disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Today
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative${className ? ` ${className}` : ''}`}>
      <div
        ref={triggerRef}
        className={`
          w-full flex items-center rounded-xl bg-surface-primary border
          transition-all overflow-hidden
          ${size === 'sm' ? 'gap-1.5 px-2 py-1.5 text-xs' : 'gap-2.5 px-3 py-2.5 text-sm'}
          ${value || inputFocused ? (size === 'sm' ? 'pr-6' : 'pr-8') : ''}
          ${open || inputFocused ? 'border-brand/40 bg-surface-secondary' : 'border-subtle hover:border-default'}
        `}
      >
        <button
          type="button"
          onClick={() => {
            setOpen(o => !o);
            inputRef.current?.focus();
          }}
          aria-label="Open calendar"
          className="text-content-muted hover:text-content-secondary shrink-0 cursor-pointer border-none outline-none bg-transparent p-0 flex"
        >
          <CalendarDays size={size === 'sm' ? 13 : 15} />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          placeholder={placeholder || 'Select or type date...'}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => {
            setInputFocused(true);
            openCalendar();
            // Show editable raw value while focused for easier rewriting
            if (value) setInputText(value);
          }}
          onBlur={() => {
            setInputFocused(false);
            commitTypedInput();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTypedInput();
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          className={`
            flex-1 min-w-0 bg-transparent border-none outline-none
            text-content placeholder:text-content-faint
            ${size === 'sm' ? 'text-xs' : 'text-sm'}
          `}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {(value || (inputFocused && inputText)) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
            setInputText('');
            inputRef.current?.focus();
          }}
          className={`
            absolute top-1/2 -translate-y-1/2 right-2.5
            text-content-muted hover:text-content transition-colors
            rounded cursor-pointer border-none outline-none bg-transparent
          `}
          aria-label="Clear date"
        >
          <X size={size === 'sm' ? 11 : 13} />
        </button>
      )}

      {calendarDropdown}
    </div>
  );
};

export default React.memo(DatePicker);
